import { v4 as uuidv4 } from 'uuid';
import { getHassNodeSchema } from '../Hasp/properties/v2/hass/index.js';
import { AutomationAction } from '../automation/ast/AutomationAction.js';
import { AutomationBinding } from '../automation/ast/AutomationBinding.js';
import { AutomationValue } from '../automation/ast/AutomationValue.js';
import { buildMediaPlayerTtsAction } from '../automation/mediaPlayerTtsAction.js';

// Node id prefix per node kind.
export const HASS_ID_PREFIX = Object.freeze({ entity: 'en', binding: 'bd', switch: 'sw', trigger: 'tr', condition: 'cd', action: 'ha' });

const withoutId = ({ id, ...rest }) => rest;

/**
 * A new action of one of the action editor's task types (ActionEditor.vue
 * TASK_TYPES), as JSON without id: what that editor builds when the task
 * type is chosen. Target entities come from wires, so targets start empty.
 */
export function createTaskAction(taskType) {
    if (taskType === 'delay') {
        return withoutId(new AutomationAction({
            type: 'delay', parameters: [{ key: 'seconds', value: AutomationValue.literal('1') }],
        }).toJSON());
    }
    if (taskType === 'media_player') return withoutId(buildMediaPlayerTtsAction({ entityId: '', text: '' }).toJSON());
    if (taskType === 'play_media') {
        return withoutId(new AutomationAction({
            type: 'call_service', provider: 'media_player', service: 'play_media', target: { entity_id: '' },
        }).toJSON());
    }
    const scene = taskType === 'activate_scene';
    const presetService = ['toggle', 'turn_on', 'turn_off'].includes(taskType) ? taskType : '';
    return withoutId(new AutomationAction({
        type: 'call_service',
        provider: scene ? 'scene' : '',
        service: scene ? 'turn_on' : presetService,
    }).toJSON());
}

/** A new binding of `strategy`, as JSON without id and entity (the entity comes from a wire). */
export function createBindingData(strategy) {
    const { id, entityId, ...data } = new AutomationBinding({ strategy, property: '' }).toJSON();
    return data;
}

/**
 * Create a node of a schema-declared type. Its data has the shape of the
 * matching automation AST unit as JSON (see flowNodes.js). An action preset
 * may name a `taskType` (see HASS_ACTION_VARIANTS) instead of data.
 */
export function createHassNode(type, { x = 0, y = 0, preset = {}, id } = {}) {
    const schema = getHassNodeSchema(type);
    if (!schema) throw new Error(`Unknown Home Assistant node type "${type}"`);
    const { taskType, ...rest } = preset ?? {};
    let data;
    if (schema.kind === 'binding') data = createBindingData(schema.strategy);
    else if (schema.kind === 'action') data = createTaskAction(type === 'delay' ? 'delay' : taskType ?? 'advanced');
    else data = structuredClone(schema.defaults ?? {});
    return {
        id: id ?? `${HASS_ID_PREFIX[schema.kind]}-${uuidv4()}`,
        kind: schema.kind,
        type,
        x,
        y,
        data: { ...data, ...structuredClone(rest) },
    };
}
